import { Caption, Paragraph2 } from "@bounce/design-system"
import { formatStripeAmount, useLocale, useTranslation } from "@bounce/i18n"
import { useStoreNickname, useStoreTypeText } from "@bounce/store"
import { Separator } from "@bounce/web-components"

import { StoreCardImage } from "./components/StoreCardImage"
import { StoreCardLiveSinceCaption } from "./components/StoreCardLiveSinceCaption"
import { StoreCardPoisSection } from "./components/StoreCardPoisSection"
import { StoreCardRating } from "./components/StoreCardRating"
import { StoreCardReservationsSection } from "./components/StoreCardReservationsSection"
import { StoreCardReview } from "./components/StoreCardReview"
import { StoreCardReviewReasonsSection } from "./components/StoreCardReviewReasonsSection"
import { LiveStoreCardCapacityCaption } from "@/components/LuggageStorage/Store/StoreCard/components/StoreCardCapacityCaption"
import type { NearbyStorageSpot } from "@/libs/bounce/stores/types"
import type { PageType } from "@/types"

const MIN_FEEDBACK_LENGTH = 15

type StoreCardProps = {
  store: NearbyStorageSpot
  from: PageType
}

export const StoreCard = ({ store, from }: StoreCardProps) => {
  const { t, i18n } = useTranslation()
  const type = useStoreTypeText(store.type)
  const nickname = useStoreNickname({ store })
  const { language } = useLocale()

  const price = formatStripeAmount(store.lowestPrice, store.city.currency, i18n.language)
  // Make sure we don't duplicate the reviews displayed across marketing pages
  const reviewPosition = from === "City" ? 0 : 1
  const review =
    store.reviews.edges
      .filter(edge => edge.node.feedback && edge.node.feedback.length >= MIN_FEEDBACK_LENGTH)
      .filter(edge => edge.node.language === language)
      .at(reviewPosition)?.node ?? undefined

  return (
    <div className="rounded-2xl text-black shadow-lg">
      <div className="relative z-1 rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-col md:flex-row">
          <StoreCardImage store={store} nickname={nickname} />
          <div className="mt-2 md:ml-5 md:mt-0">
            <Caption className="text-neutral-800">{type}</Caption>
            <p className="title5 mt-1">{`Bounce -  ${nickname}`}</p>
            <div className="mt-2 flex flex-row items-center gap-2">
              <StoreCardRating store={store} />
              <Paragraph2 className="!text-[0.875rem]">
                {t("cmp.storeCard.priceLabel", "from {price}/bag", { price })}
              </Paragraph2>
            </div>
            <div className="mt-4 flex flex-col gap-2 md:flex-row md:gap-4">
              <StoreCardLiveSinceCaption firstLiveAt={store.firstLiveAt} />
              <LiveStoreCardCapacityCaption
                maxCapacity={store.maxCapacity}
                last7dReservationCount={store.last7dReservationCount}
              />
            </div>
            <Separator className="my-4" />
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
              <StoreCardPoisSection coordinates={store.coordinates} nickname={nickname} pois={store.pois} />
              <StoreCardReservationsSection
                last7dReservationCount={store.last7dReservationCount}
                last30dReservationCount={store.last30dReservationCount}
                last365dReservationCount={store.last365dReservationCount}
                totalReservationCount={store.totalReservationCount}
              />
              {!!store.reviewSummaryText && (
                <StoreCardReviewReasonsSection reviewSummaryText={store.reviewSummaryText} />
              )}
            </div>
          </div>
        </div>
      </div>
      {!!store.rating && !!review && <StoreCardReview rating={store.rating} review={review} />}
    </div>
  )
}
